import { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, XIcon } from "../../../../../assets/icons";
import "./index.scss";

type NavigateDirection = "next" | "previous";

interface FindBarProps {
  onSearch?: (searchTerm: string, options: SearchOptions) => void;
  onNavigate?: (direction: NavigateDirection) => void;
  totalResults?: number;
  currentResult?: number;
  isOpen?: boolean;
  onClose?: () => void;
  placeholder?: string;
}

interface SearchOptions {
  caseSensitive: boolean;
  wholeWord: boolean;
}

export const FindBar = ({
  onSearch,
  onNavigate,
  totalResults = 0,
  currentResult = 0,
  isOpen = false,
  onClose,
  placeholder = "Buscar...",
}: FindBarProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      onSearch?.(value, { caseSensitive: false, wholeWord: false });
    },
    [onSearch]
  );

  const handleNext = useCallback(() => {
    onNavigate?.("next");
  }, [onNavigate]);

  const handlePrevious = useCallback(() => {
    onNavigate?.("previous");
  }, [onNavigate]);

  const handleClose = useCallback(() => {
    setSearchTerm("");
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="pdl_component_findbar">
      <div className="findbar-container">
        <div className="findbar-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            className="findbar-input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleNext();
              } else if (e.key === "Enter" && e.shiftKey) {
                e.preventDefault();
                handlePrevious();
              }
            }}
          />
          {searchTerm && (
            <div className="findbar-results">
              {currentResult > 0 && totalResults > 0 ? (
                <>
                  {currentResult} de {totalResults}
                </>
              ) : (
                "Nenhum resultado"
              )}
            </div>
          )}
        </div>

        <div className="findbar-controls">
          <button
            className="findbar-button"
            onClick={handlePrevious}
            disabled={!searchTerm || totalResults === 0}
            title="Anterior (Shift+Enter)"
          >
            <ChevronLeft />
          </button>

          <button
            className="findbar-button"
            onClick={handleNext}
            disabled={!searchTerm || totalResults === 0}
            title="Próximo (Enter)"
          >
            <ChevronRight />
          </button>

          <button
            className="findbar-button findbar-close"
            onClick={handleClose}
            title="Fechar (Esc)"
          >
            <XIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FindBar;
export type { FindBarProps, SearchOptions, NavigateDirection };
